/* eslint-disable */
/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || | 
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2023 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React from "react";
import { NavLink } from "react-router-dom";
// Chakra imports
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { FaUser, FaKey } from "react-icons/fa";
import { useAuth } from 'hooks/useAuth';
import { useState } from 'react';
import { useToastNotification } from 'utils/toast-utils';
import { useFormik } from 'formik';
import { loginSchema } from '../schemas';  // Update path according to your structure
import { ReactComponent as Logo } from "assets/img/layout/Logor.svg";

function SignIn() {
  const { login, error, loading } = useAuth();
  const { showToast } = useToastNotification();
  const [show, setShow] = React.useState(false);
  
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      try {
        const response = await login(values.email, values.password);
        console.log("🚀 ~ onSubmit: ~ response:", response)
        if (response) {
          showToast(
            'Success',
            'Logged in successfully!',
            'success'
          );
        }
      } catch (err) {
        console.log("🚀 ~ onSubmit: ~ err:", err)
        showToast(
          'Error',
          err.message || err.response?.data?.message || err || 'Failed to login',
          'error'
        );
      }
    }
  });

  // Chakra color mode
  const textColor = useColorModeValue("#42505C", "white");
  const textColorSecondary = "#42505C";
  const textColorDetails = useColorModeValue("#42505C", "secondaryGray.600");
  const textColorBrand = "#602CE8";
  const brandStars = useColorModeValue("#42505C", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("#42505C", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection='column'>
        <Box me='auto' w={{ base: "100%", md: "420px" }}>
          <Box mb="24px">
            <Logo />
          </Box>
          <Heading 
            color={textColor} 
            fontSize='54px' 
            mb='10px'
            fontFamily="'Roboto', sans-serif"
          >
            Log In
          </Heading>
          <Text
            mb='36px'
            ms='4px'
            color="#404040"
            fontWeight='400'
            fontSize='14px'
            lineHeight="2"
            >
            Please input your information in the fields below to enter your Journey platform. 
          </Text>
        </Box>
        <Flex
          zIndex='2'
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          {/* <Flex align='center' mb='25px'>
            <HSeparator />
          </Flex> */}
          <FormControl as="form" onSubmit={formik.handleSubmit}>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="46px">
                <FaUser color="lightgrey" />
              </InputLeftElement>
              <Input
                variant='auth'
                fontSize='sm'
                ms={{ base: "0px", md: "0px" }}
                type='email'
                placeholder='mail@example.com'
                mb={formik.touched.email && formik.errors.email ? '0' : '24px'}
                fontWeight='500'
                size='lg'
                width="472px"
                height="48px"
                borderRadius="6px"
                border="1px solid"
                pl="40px"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.email && formik.errors.email}
              />
            </InputGroup>
            {formik.touched.email && formik.errors.email && (
              <Text color="red.500" fontSize="sm" mb="24px">
                {formik.errors.email}
              </Text>
            )}

            <FormLabel
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              display='flex'>
              Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none" h="46px">
                <FaKey color="lightgrey" />
              </InputLeftElement>
              <Input
                fontSize='sm'
                placeholder='Min. 8 characters'
                mb={formik.touched.password && formik.errors.password ? '0' : '24px'}
                size='lg'
                type={show ? "text" : "password"}
                variant='auth'
                width="472px"
                height="48px"
                borderRadius="6px"
                border="1px solid"
                pl="40px"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.password && formik.errors.password}
              />
              <InputRightElement display='flex' alignItems='center' mt='4px'>
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={() => setShow(!show)}
                />
              </InputRightElement>
            </InputGroup>
            {formik.touched.password && formik.errors.password && (
              <Text color="red.500" fontSize="sm" mb="24px">
                {formik.errors.password}
              </Text>
            )}

            <Flex justifyContent='space-between' align='center' mb='24px'>
              <FormControl display='flex' alignItems='center'>
                <Checkbox
                  id='rememberMe'
                  colorScheme='brandScheme'
                  me='10px'
                  name="rememberMe"
                  isChecked={formik.values.rememberMe}
                  onChange={formik.handleChange}
                />
                <FormLabel
                  htmlFor='rememberMe'
                  mb='0'
                  fontWeight='normal'
                  color={textColor}
                  fontSize='sm'>
                  Remember me
                </FormLabel>
              </FormControl>
              <NavLink to='/auth/forgot-password'>
                <Text
                  color={textColorBrand}
                  fontSize='sm'
                  w='124px'
                  fontWeight='500'>
                  Forgot password?
                </Text>
              </NavLink>
            </Flex>

            <Button
              fontSize='sm'
              variant='brand'
              fontWeight='500'
              w='100%'
              h='50'
              mb='24px'
              background='#602CE8'
              borderRadius="6px"
              borderWidth="1px"
              _hover={{ bg: "#5835A8" }}
              type="submit"
              isLoading={loading}
              isDisabled={!formik.isValid || loading}
            >
              Log In
            </Button>
          </FormControl>
          <Flex
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            maxW='100%'
            mt='0px'>
            <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
              Don't have an account yet?
              <NavLink to='/auth/register'>
                <Text
                  color={textColorBrand}
                  as='span'
                  ms='5px'
                  fontWeight='500'>
                  Register here
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
